import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/images/fgfgv-v.png';
import styles from './Navbar.module.css';

const NAV_CONFIG = [
  { id: 'home', path: '/', labelKey: 'nav.home' },
  { id: 'about', path: '/about', labelKey: 'nav.about' },
  { id: 'board', path: '/board-of-directors', labelKey: 'nav.board' },
  { id: 'products', path: '/products', labelKey: 'nav.products' },
  { id: 'investor', path: '/investor-relations', labelKey: 'nav.investor' },
  { id: 'factory', path: '/our-factory', labelKey: 'nav.factory' },
  { id: 'partners', path: '/success-partners', labelKey: 'nav.partners' },
  {
    id: 'media',
    labelKey: 'nav.mediaGroup',
    children: [
      { path: '/media-center', labelKey: 'nav.media' },
      { path: '/news', labelKey: 'nav.news' },
      { path: '/gallery', labelKey: 'nav.gallery' },
    ],
  },
  { id: 'faq', path: '/faq', labelKey: 'nav.faq' },
  { id: 'contact', path: '/contact-us', labelKey: 'nav.contact' },
];

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState(null);
  const logoBarRef = useRef(null);
  const topBarRef = useRef(null);
  const navbarRef = useRef(null);
  const scrolledRef = useRef(false);

  const updateLogoBarHeight = useCallback(() => {
    if (logoBarRef.current && topBarRef.current && navbarRef.current) {
      const topBarHeight = topBarRef.current.offsetHeight;
      const logoBarHeight = logoBarRef.current.offsetHeight;
      const totalOffset = topBarHeight + logoBarHeight;
      navbarRef.current.style.setProperty('--logo-bar-height', `${totalOffset}px`);
    }
  }, []);

  useEffect(() => {
    updateLogoBarHeight();
    window.addEventListener('resize', updateLogoBarHeight);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(updateLogoBarHeight);
    }
    return () => window.removeEventListener('resize', updateLogoBarHeight);
  }, [updateLogoBarHeight]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const wasScrolled = scrolledRef.current;
          let newScrolled = wasScrolled;
          if (wasScrolled && currentScrollY < 10) newScrolled = false;
          else if (!wasScrolled && currentScrollY > 50) newScrolled = true;
          if (newScrolled !== wasScrolled) {
            scrolledRef.current = newScrolled;
            setScrolled(newScrolled);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toArabicNumerals = (text) => {
    if (i18n.language !== 'ar') return text;
    const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return text.replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
  };

  // Sync dir & lang attributes whenever the language changes (including on first mount)
  useEffect(() => {
    document.body.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  };

  const isActive = (path) => (location.pathname === path ? styles.navLinkActive : '');

  const isGroupActive = (item) =>
    item.children?.some((child) => location.pathname === child.path) ?? false;

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setMobileOpenDropdown(null);
  };

  const toggleMobileDropdown = (id) => {
    setMobileOpenDropdown((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMenuOpen]);

  const renderNavItems = () => {
    const items = i18n.language === 'ar' ? [...NAV_CONFIG].reverse() : NAV_CONFIG;
    return items.map((item) => {
      if (item.children) {
        const groupActive = isGroupActive(item);
        const mobileOpen = isMenuOpen && mobileOpenDropdown === item.id;

        return (
          <div
            key={item.id}
            className={`${styles.navDropdownWrapper} ${groupActive ? styles.navDropdownActive : ''}`}
          >
            <button
              className={`${styles.navDropdownTrigger} ${groupActive ? styles.navDropdownTriggerActive : ''}`}
              onClick={() => isMenuOpen && toggleMobileDropdown(item.id)}
              aria-expanded={mobileOpen}
            >
              <span className={styles.triggerLabel}>{t(item.labelKey)}</span>
              <svg
                className={`${styles.chevron} ${mobileOpen ? styles.chevronOpen : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            <div
              className={`${styles.navDropdownMenu} ${mobileOpen ? styles.navDropdownMenuMobileOpen : ''}`}
            >
              {item.children.map((child) => (
                <Link
                  key={child.path}
                  to={child.path}
                  className={`${styles.navDropdownItem} ${isActive(child.path)}`}
                  onClick={closeMenu}
                >
                  {t(child.labelKey)}
                </Link>
              ))}
            </div>
          </div>
        );
      }

      return (
        <Link
          key={item.id}
          to={item.path}
          className={`${styles.navLink} ${isActive(item.path)}`}
          onClick={closeMenu}
        >
          <span>{t(item.labelKey)}</span>
        </Link>
      );
    });
  };

  return (
    <nav ref={navbarRef} className={`${styles.navbar} ${scrolled ? styles.navbarScrolled : ''}`}>
      {/* Top Bar */}
      <div ref={topBarRef} className={styles.navbarTop}>
        <div className={styles.navbarContainer}>
          <div className={styles.contactInfo}>
            <a href={`tel:${t('contact.phone1')}`} className={styles.contactItem}>
              <svg className={styles.icon} fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              <span className={styles.contactText}>{toArabicNumerals(t('contact.phone1'))}</span>
            </a>
            <a href={`tel:${t('contact.phone2')}`} className={styles.contactItem}>
              <svg className={styles.icon} fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              <span className={styles.contactText}>{toArabicNumerals(t('contact.phone2'))}</span>
            </a>
            <a href={`mailto:${t('contact.email')}`} className={styles.contactItem}>
              <svg className={styles.icon} fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              <span className={styles.contactText}>{t('contact.email')}</span>
            </a>
          </div>
          <div className={styles.topBarRight}>
            <button className={styles.languageToggleTop} onClick={toggleLanguage}>
              <span>{i18n.language === 'en' ? 'العربية' : 'English'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Logo Bar */}
      <div ref={logoBarRef} className={styles.logoBar}>
        <div className={styles.navbarContainer}>
          <Link to="/" className={styles.logoLink}>
            <img src={logo} alt="Al Nasser Group" className={styles.logo} />
          </Link>
          <button className={styles.menuToggle} onClick={toggleMenu} aria-label="Toggle menu">
            <span className={`${styles.hamburger} ${isMenuOpen ? styles.hamburgerOpen : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Backdrop */}
      {isMenuOpen && <div className={styles.menuBackdrop} onClick={closeMenu}></div>}

      {/* Navigation Menu */}
      <div className={styles.navbarMain}>
        <div className={styles.navbarContainer}>
          <div className={`${styles.navbarMenu} ${isMenuOpen ? styles.navbarMenuOpen : ''} ${i18n.language === 'ar' ? styles.navbarMenuRtl : ''}`}>
            {isMenuOpen && (
              <button className={styles.menuCloseButton} onClick={closeMenu} aria-label="Close menu">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
            {renderNavItems()}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
